import { ActionMenu, Button, icons, RadixTabs } from '@portal/components'
import dayjs from 'dayjs'
import isYesterday from 'dayjs/plugin/isYesterday'
import React, { useCallback, useState } from 'react'

import {
  QueryHistoryItem,
  useQueryFavorites,
  useQueryHistory,
  useQueryHistoryStore,
} from '../../store'
import { useDQLEditorContext } from '../dqlEditor/context'

const { MenuHorizontal, InformationCircle } = icons

dayjs.extend(isYesterday)

export default function QueryHistoryList() {
  const { setQuery, appId } = useDQLEditorContext()

  const queries = useQueryHistory(appId || '')
  const favorites = useQueryFavorites(appId || '')
  const { toggleFavorite, removeQuery } = useQueryHistoryStore()

  const [tab, setTab] = useState<'favorites' | 'queries'>('favorites')

  const handleTabChange = (value: string) => {
    if (value === 'queries' || value === 'favorites') {
      setTab(value)
    }
  }

  const handleSetQuery = useCallback(
    (query: string) => {
      setQuery(query, false)
    },
    [setQuery],
  )

  const handleToggleFavorite = useCallback(
    (query: QueryHistoryItem) => {
      toggleFavorite(query)
    },
    [toggleFavorite],
  )

  const handleDelete = useCallback(
    (query: QueryHistoryItem) => {
      removeQuery(query)
    },
    [removeQuery],
  )

  const timeSinceLastUsed = (lastUsed: number) => {
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    const lastUsedDate = new Date(lastUsed)

    // Check if date is yesterday or before yesterday
    const isYesterday = dayjs(lastUsedDate).isYesterday()
    const isBeforeYesterday = dayjs(lastUsedDate).isBefore(yesterday)

    if (isYesterday) {
      return `Yesterday ${dayjs(lastUsedDate).format('HH:mm')}`
    } else if (isBeforeYesterday) {
      return `${dayjs(lastUsedDate).format('MMM D, HH:mm')}`
    } else {
      // For today, use relative time
      const timeDiff = Date.now() - lastUsed
      const seconds = Math.floor(timeDiff / 1000)
      const minutes = Math.floor(seconds / 60)
      const hours = Math.floor(minutes / 60)

      if (hours > 1) {
        return `${hours} hours ago`
      } else if (hours === 1) {
        return `1 hour ago`
      } else if (minutes > 1) {
        return `${minutes} minutes ago`
      } else if (minutes === 1) {
        return `1 minute ago`
      } else {
        return `Just now`
      }
    }
  }

  const EmptyList = ({ message }: { message: string }) => {
    return (
      <div className="flex items-center gap-2 p-4 text-foreground-subtle">
        <InformationCircle className="size-5" />
        <p className="font-medium">{message}</p>
      </div>
    )
  }

  const ListItem = ({ query }: { query: QueryHistoryItem }) => {
    return (
      <div
        role="listitem"
        key={query.query}
        className="group flex w-full flex-col items-center rounded-lg py-1 pb-2 pl-3 pr-1 text-base hover:bg-background-surface"
      >
        <div className="flex w-full items-center justify-between gap-2">
          <span
            className="line-clamp-1 w-full cursor-pointer pb-0.5"
            onClick={() => handleSetQuery(query.query)}
          >
            {timeSinceLastUsed(query.lastUsed)}
          </span>

          <div className="flex items-center gap-2">
            <ActionMenu
              items={[
                {
                  label: 'Favorite',
                  icon: query.isFavorite ? 'Check' : undefined,
                  onClick: () => handleToggleFavorite(query),
                },
                {
                  label: 'Delete',
                  onClick: () => handleDelete(query),
                },
              ]}
              trigger={
                <Button
                  variant="ghost"
                  size="icon"
                  className="hover:bg-background-surface-secondary"
                >
                  <MenuHorizontal className="h-4 w-4" />
                </Button>
              }
              align="start"
            />
          </div>
        </div>

        <div
          className="w-full cursor-pointer truncate text-foreground-subtle"
          onClick={() => handleSetQuery(query.query)}
        >
          {query.query}
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="p-2">
        <RadixTabs
          defaultValue="favorites"
          value={tab}
          onValueChange={handleTabChange}
          className="w-full"
        >
          <RadixTabs.List className="w-full">
            <RadixTabs.Trigger value="favorites" className="w-full">
              Favorites
            </RadixTabs.Trigger>
            <RadixTabs.Trigger value="queries" className="w-full">
              History
            </RadixTabs.Trigger>
          </RadixTabs.List>
        </RadixTabs>
      </div>

      {queries.length === 0 && tab === 'queries' && (
        <EmptyList message="Run a query to save it to history" />
      )}

      {queries.length > 0 && tab === 'queries' && (
        <div
          role="list"
          className="flex flex-1 flex-col gap-2 overflow-auto px-2 pb-2"
        >
          {queries.map((query) => (
            <ListItem key={query.query} query={query} />
          ))}
        </div>
      )}

      {favorites.length === 0 && tab === 'favorites' && (
        <EmptyList message="Your favorite queries appear here" />
      )}

      {favorites.length > 0 && tab === 'favorites' && (
        <div
          role="list"
          className="flex flex-1 flex-col gap-2 overflow-auto px-2 pb-2"
        >
          {favorites.map((query) => (
            <ListItem key={query.query} query={query} />
          ))}
        </div>
      )}
    </>
  )
}
